import { useMemo } from 'react';
import { useGetCampaignLeaderboard } from '@/hooks/challenges';
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from '@/config/campaign';
import { LeaderboardPeriodType } from '@/types';

interface LeaderboardEntry {
  address: string;
  campaignId: string;
  campaignGroup: string;
  totalPoints: string;
  rank: number;
  isVerified: boolean;
  hasPremiumSubscription: boolean;
}

export interface LeaderboardUser {
  rank: number;
  address?: string;
  points: number;
}

export const useLeaderboard = (
  address: string = '',
  limit: number = 10,
  currentPage: number = 0,
  periodType: LeaderboardPeriodType = LeaderboardPeriodType.ALL
) => {
  const { data: leaderboardData, isLoading } = useGetCampaignLeaderboard(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID,
    address,
    limit,
    periodType,
    currentPage
  );

  const leaderboard = useMemo(() => {
    if (!leaderboardData?.data) return [];
    
    return leaderboardData.data.map((entry: LeaderboardEntry) => {
      // Use the rank from the API response directly
      return {
        rank: entry.rank,
        address: entry.address,
        points: parseFloat(entry.totalPoints || "0")
      } as LeaderboardUser;
    });
  }, [leaderboardData]);

  return {
    leaderboard,
    isLoading,
    hasNextPage: leaderboard.length === limit,
    hasPrevPage: currentPage > 0
  };
};

export const truncateAddress = (address: string) => {
  if (!address) return 'Anonymous';
  if (address.startsWith('0x') && address.length > 10) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }
  return address;
};

export const getDisplayName = (user: LeaderboardUser) => {
  return truncateAddress(user?.address || 'Anonymous');
};
